import { User } from 'lucide-react'
import Image from 'next/image'
import * as React from 'react'

import { type NonFsdsTheme,resolveNonFsdsTheme } from '@/lib/non-fsds-theme'
import { useTenantMediaDefaults } from '@/hooks/use-tenant-media-defaults'

export interface NonFsdsFeedbackQuote {
  value: string
  avatar?: string
}

interface NonFsdsFeedbackSectionProps {
  title: string
  quotes: (string | NonFsdsFeedbackQuote)[]
  isEnglish: boolean
  theme?: NonFsdsTheme | string
}

export default function NonFsdsFeedbackSection({ title, quotes, isEnglish, theme = 'general' }: NonFsdsFeedbackSectionProps) {
  const { resolveAvatarUrl } = useTenantMediaDefaults()
  const themeStyles = {
    general: {
      cardBg: 'from-blue-50/80', cardRing: 'ring-blue-100', cardShadow: 'hover:shadow-blue-100/50',
      avatarBorder: 'border-blue-200', verifiedText: 'text-blue-600', quoteIcon: 'text-blue-100',
    },
    'english-academy': {
      cardBg: 'from-blue-50/80', cardRing: 'ring-emerald-100', cardShadow: 'hover:shadow-emerald-100/50',
      avatarBorder: 'border-emerald-200', verifiedText: 'text-blue-600', quoteIcon: 'text-emerald-100',
    },
    programming: {
      cardBg: 'from-indigo-50/80', cardRing: 'ring-indigo-100', cardShadow: 'hover:shadow-indigo-100/50',
      avatarBorder: 'border-indigo-200', verifiedText: 'text-indigo-600', quoteIcon: 'text-indigo-100',
    },
    math: {
      cardBg: 'from-teal-50/80', cardRing: 'ring-teal-100', cardShadow: 'hover:shadow-teal-100/50',
      avatarBorder: 'border-teal-200', verifiedText: 'text-teal-600', quoteIcon: 'text-teal-100',
    },
  }[resolveNonFsdsTheme(theme)]

  return (
    <section data-testid="non-fsds-feedback-section" className="bg-white px-4 py-16 sm:px-6 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">{title}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-slate-600">
            {isEnglish
              ? 'Trusted by learners who need clear guidance, fast feedback, and measurable progress.'
              : 'Được tin tưởng bởi học viên cần định hướng rõ ràng, phản hồi nhanh và tiến bộ đo lường được.'}
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:gap-8">
          {quotes.map((item, index) => {
            const quote = typeof item === 'string' ? item : item.value
            const avatar = typeof item === 'object' && item.avatar ? resolveAvatarUrl(item.avatar) : resolveAvatarUrl(null)
            return (
              <article key={`${quote}-${index}`} className={`group relative rounded-[24px] bg-gradient-to-b to-slate-50/50 p-8 shadow-sm ring-1 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${themeStyles.cardBg} ${themeStyles.cardRing} ${themeStyles.cardShadow}`}>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className={`absolute -inset-1 rounded-full border-2 border-dashed opacity-0 transition-opacity duration-300 group-hover:opacity-100 ${themeStyles.avatarBorder}`} />
                    {avatar ? (
                      <Image src={avatar} alt="" width={56} height={56} className="relative size-14 rounded-full object-cover shadow-sm ring-2 ring-white" />
                    ) : (
                      <div className="relative flex size-14 items-center justify-center rounded-full bg-slate-100 text-slate-400 shadow-sm ring-2 ring-white">
                        <User className="size-6" aria-hidden="true" />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-base font-bold text-slate-900">{isEnglish ? 'Learner from community' : 'Học viên trong cộng đồng'}</p>
                    <p className={`mt-0.5 flex items-center gap-1.5 text-sm font-semibold ${themeStyles.verifiedText}`}>
                      <svg className="size-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.403 12.652a3 3 0 000-5.304 3 3 0 00-3.75-3.751 3 3 0 00-5.305 0 3 3 0 00-3.751 3.75 3 3 0 000 5.305 3 3 0 003.75 3.751 3 3 0 005.305 0 3 3 0 003.751-3.75zm-2.546-4.46a.75.75 0 00-1.214-.883l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" /></svg>
                      {isEnglish ? 'Verified learning track' : 'Lộ trình học đã xác minh'}
                    </p>
                  </div>
                </div>
                <blockquote className="relative mt-6 text-lg leading-relaxed text-slate-700">
                  <svg className={`absolute -left-3 -top-3 size-8 -translate-x-full opacity-60 transition-transform duration-300 group-hover:-translate-y-1 ${themeStyles.quoteIcon}`} fill="currentColor" viewBox="0 0 32 32" aria-hidden="true"><path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" /></svg>
                  &ldquo;{quote}&rdquo;
                </blockquote>
              </article>
            )
          })}
        </div>
      </div>
    </section>
  )
}
